import { useEffect, useState } from "react";
import { api, ApiRequestError } from "./api.ts";
import type { Me } from "./types.ts";

type State =
  | { status: "loading" }
  | { status: "authenticated"; me: Me }
  | { status: "unauthenticated" }
  | { status: "forbidden"; message: string }
  | { status: "error"; message: string };

/**
 * Resolves the current user via GET /api/me, which upserts the users row on
 * first login and assigns role (admin if in the signage-admins Entra group).
 * A 401 means the Entra session isn't established — send the user to /login.
 * A 403 means they signed in successfully but with an account outside the
 * church domain (the tenant holds B2B guests) — that needs its own screen, not
 * a login redirect, because bouncing them to /login would just loop them back
 * in on the same account.
 */
export function useMe(): State {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    api
      .get<Me>("/me")
      .then((me) => alive && setState({ status: "authenticated", me }))
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiRequestError && err.status === 401) {
          setState({ status: "unauthenticated" });
        } else if (err instanceof ApiRequestError && err.status === 403) {
          setState({ status: "forbidden", message: err.message });
        } else {
          const message =
            err instanceof Error ? err.message : "Failed to load account";
          setState({ status: "error", message });
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}
